import {
    MAIN_ROOM_ID,
    type InfinityClient,
    type InfinitySignals,
    type Participant,
    type ParticipantID,
    type ParticipantSignals,
    type RoomID,
    type RoomParticipantEvent,
} from '@pexip/infinity';
import type {Media} from '@pexip/media';
import type {Signal} from '@pexip/signal';
import type {InMeetingParticipant} from '@pexip/media-components';
import {difference} from '@pexip/utils';

import type {
    GetSignalType,
    JoinActivity,
    LeaveActivity,
    UpdateActivity,
    ParticipantActivity,
    RoomParticipantActivity,
    TransferActivity,
    TransferTarget,
} from '../types';
import {ParticipantActivities} from '../types';
import {createReversedMapObject} from './createReversedMapObject';
import {createConsumableKeyedBuffer} from './createConsumableKeyedBuffer';
import {
    createReversedMapSet,
    type ReversedMapSet,
} from './createReversedMapSet';
import {ParticipantSearcher} from './participantSearcher';

export type ParticipantsFilterKey =
    | 'waitingInLobby'
    | 'external'
    | 'raisedHand'
    | 'inMeeting'
    | 'breakout'
    | 'chair'
    | 'guest'
    | 'fecc'
    | 'directChat'
    | 'none';
export type ParticipantsSortKey = 'startTime' | '';
export interface ParticipantsGetOptions {
    participantID: ParticipantID;
    roomId: RoomID;
    filterBy: ParticipantsFilterKey;
    searchQuery?: string;
    caching: boolean;
}
export type ClientAPIs = Pick<
    InfinityClient,
    | 'admit'
    | 'conferenceStatus'
    | 'getMe'
    | 'kick'
    | 'mute'
    | 'raiseHand'
    | 'roomId'
    | 'setRole'
    | 'spotlight'
>;
export type GetMedia = () =>
    | Pick<Media, 'muteAudio' | 'muteVideo' | 'audioMuted' | 'videoMuted'>
    | undefined;
export type InMeetingParticipantSignals = Pick<
    ParticipantSignals,
    | 'onParticipants'
    | 'onParticipantJoined'
    | 'onParticipantUpdated'
    | 'onParticipantLeft'
> &
    Pick<InfinitySignals, 'onBreakoutEnd'>;

export type RoomParticipantMap = Map<RoomID, Map<ParticipantID, Participant>>;

type CacheKey<DataType> = string & {__dataType: DataType};
type GroupCacheKey = CacheKey<'group'>;
type BreakoutCacheKey = CacheKey<'breakout'>;

// Ensure we have exactly the same empty participant list, and make sure it
// cannot be altered.
const EMPTY_SET = new Set<ParticipantID>();

const participantSearcher = new ParticipantSearcher();

export const joinActivity = (pid: ParticipantID): JoinActivity => ({
    activity: ParticipantActivities.Join,
    participantId: pid,
});
export const leaveActivity = (pid: ParticipantID): LeaveActivity => ({
    activity: ParticipantActivities.Leave,
    participantId: pid,
});
export const updateActivity = (pid: ParticipantID): UpdateActivity => ({
    activity: ParticipantActivities.Update,
    participantId: pid,
});
export const transferActivity = (
    pid: ParticipantID,
    name: string,
    target: TransferTarget,
): TransferActivity => ({
    activity: ParticipantActivities.Transfer,
    participantId: pid,
    destinationType: target,
    destination: name,
});

const toGroupCacheKey = (
    group: ParticipantsFilterKey,
    roomId: RoomID,
    participantID?: ParticipantID,
) =>
    ['group', roomId, group, participantID]
        .flatMap(key => (key ? [JSON.stringify(key)] : []))
        .join(':') as GroupCacheKey;
const toBreakoutCacheKey = (
    group: ParticipantsFilterKey,
    roomId: RoomID,
    participantID?: ParticipantID,
) =>
    ['breakout', roomId, group, participantID]
        .flatMap(key => (key ? [JSON.stringify(key)] : []))
        .join(':') as BreakoutCacheKey;

export const isWaitingToBeAdmitted = (participant: InMeetingParticipant) => {
    return participant.isWaiting || participant.serviceType === 'ivr';
};

export const isAPIHost = (participant: Participant) => {
    return participant.role === 'chair' && !participant.hasMedia;
};

export const getDisplayName = (participant: Participant) =>
    participant.overlayText || participant.displayName;

const assignGroups = (
    participant: InMeetingParticipant,
): ParticipantsFilterKey[] => {
    const keys: ParticipantsFilterKey[] = ['none'];
    // No other assignments if the participant is transferring
    if (participant.isTransferring) {
        return keys;
    }
    if (participant.supportsDirectChat) {
        keys.push('directChat');
    }
    if (isWaitingToBeAdmitted(participant)) {
        keys.push('waitingInLobby');
    } else if (participant.isExternal) {
        keys.push('external');
    } else {
        keys.push('inMeeting');
    }
    if (participant.raisedHand) {
        keys.push('raisedHand');
    }
    if (!isAPIHost(participant)) {
        keys.push('breakout');
    }
    if (participant.isHost) {
        keys.push('chair');
    } else {
        keys.push('guest');
    }
    if (participant.canFecc) {
        keys.push('fecc');
    }
    return keys;
};

/**
 * Diff two filter sets and return the difference
 */
const diffGroups = (
    oldGroups: Set<ParticipantsFilterKey>,
    newGroups: Set<ParticipantsFilterKey>,
) => {
    const diffNew = difference(newGroups, oldGroups);
    const diffOld = difference(oldGroups, newGroups);
    return {diffNew, diffOld};
};

const createInMeetingParticipant = (
    participant: Participant,
    clientAPIs: ClientAPIs,
    getMedia: GetMedia,
    roomId: RoomID,
    highlightedCharacters?: {start: number; end: number}[],
): InMeetingParticipant => {
    const breakoutUuid = roomId === MAIN_ROOM_ID ? undefined : roomId;
    const isSelf = clientAPIs.getMe(breakoutUuid)?.uuid === participant.uuid;
    const muteAudio = isSelf
        ? () => {
              if (getMedia()?.audioMuted !== undefined) {
                  getMedia()?.muteAudio(!getMedia()?.audioMuted);
              }
          }
        : () => {
              clientAPIs.mute({
                  participantUuid: participant.uuid,
                  mute: !participant.isMuted,
                  breakoutUuid,
              });
          };
    const muteVideo = () => {
        if (getMedia()?.videoMuted !== undefined) {
            getMedia()?.muteVideo(!getMedia()?.videoMuted);
        }
    };
    const kick = () => {
        clientAPIs.kick({
            participantUuid: participant.uuid,
            breakoutUuid,
        });
    };
    const admit = () => {
        clientAPIs.admit({
            participantUuid: participant.uuid,
            breakoutUuid,
        });
        // if role is 'chair' | 'unknown', set role to 'guest' and bypass host PIN
        if (participant.role !== 'guest') {
            clientAPIs.setRole({
                role: 'guest',
                participantUuid: participant.uuid,
            });
        }
    };
    const spotlight = () => {
        clientAPIs.spotlight({
            enable: !participant.isSpotlight,
            participantUuid: participant.uuid,
            breakoutUuid,
        });
    };
    const setRole = () => {
        clientAPIs.setRole({
            role: participant.isHost ? 'guest' : 'chair',
            participantUuid: participant.uuid,
        });
    };
    const lowerHand = () => {
        clientAPIs.raiseHand({
            raise: false,
            participantUuid: participant.uuid,
            breakoutUuid,
        });
    };
    return Object.defineProperties(participant, {
        displayName: {
            value: getDisplayName(participant),
            configurable: true,
        },
        highlightedCharacters: {
            value: highlightedCharacters ?? [],
            configurable: true,
            writable: true,
        },
        mute: {value: muteAudio, configurable: true},
        muteVideo: {value: muteVideo, configurable: true},
        kick: {value: kick, configurable: true},
        admit: {value: admit, configurable: true},
        spotlight: {value: spotlight, configurable: true},
        setRole: {value: setRole, configurable: true},
        lowerHand: {value: lowerHand, configurable: true},
        roomId: {value: roomId, configurable: true},
    });
};

export const createInMeetingParticipants = (
    clientApis: ClientAPIs,
    getMedia: GetMedia,
    participantsSignals: InMeetingParticipantSignals,
    activitySignal?: Signal<RoomParticipantActivity, RoomParticipantActivity[]>,
    previous?: {
        participants?: RoomParticipantMap;
        activities?: [RoomID, Set<ParticipantActivity>][];
    },
) => {
    // -----------------------------------------------------------------------
    // Internal States
    // -----------------------------------------------------------------------
    const currentParticipants = createReversedMapObject<
        RoomID,
        ParticipantID,
        InMeetingParticipant
    >();
    const oldParticipants = createReversedMapObject<
        RoomID,
        ParticipantID,
        Participant
    >(previous?.participants);
    // Buffer for participant activities
    const activityBuffer = createConsumableKeyedBuffer<
        RoomID,
        ParticipantActivity
    >(previous?.activities);
    // Participant Groups
    const groups = new Map<
        RoomID,
        ReversedMapSet<ParticipantsFilterKey, ParticipantID>
    >();
    // Caches
    const cache = new Map<RoomID, Map<GroupCacheKey, Set<ParticipantID>>>();
    const breakoutCache = new Map<
        RoomID,
        Map<BreakoutCacheKey, Set<ParticipantID>>
    >();
    const searchResultCache = new Map<
        RoomID,
        Map<
            GroupCacheKey | BreakoutCacheKey,
            {searchQuery: string; result: Set<ParticipantID>}
        >
    >();
    const highlightedCharactersCache = new Map<
        GroupCacheKey | BreakoutCacheKey,
        Map<ParticipantID, {start: number; end: number}[]>
    >();

    // -----------------------------------------------------------------------
    // Internal Functions
    // -----------------------------------------------------------------------
    const addGroup = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        let room = groups.get(roomId);
        if (!room) {
            room = createReversedMapSet();
            groups.set(roomId, room);
        }
        if (room.has(groupKey, participant.uuid)) {
            return;
        }
        room.add(groupKey, participant.uuid);
        clearParticipantCache(participant, roomId, groupKey);
        clearGroupCache(roomId, groupKey);
    };

    const removeGroup = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        const room = groups.get(roomId);
        if (!room) {
            return false;
        }
        if (room.has(groupKey, participant.uuid)) {
            clearParticipantCache(participant, roomId, groupKey);
            clearGroupCache(roomId, groupKey);
            return room.remove(groupKey, participant.uuid);
        }
        return false;
    };

    const pushActivity = (roomId: RoomID, activity: ParticipantActivity) => {
        activityBuffer.push(roomId, activity);
        activitySignal?.emit({roomId, activity});
    };

    const setCurrentParticipant = (
        roomId: RoomID,
        participant: Participant,
    ) => {
        currentParticipants.set(roomId, participant);
        updateParticipantGroups(roomId, participant);
    };

    const getCache = (roomId: RoomID) => {
        let roomCache = cache.get(roomId);
        if (!roomCache) {
            roomCache = new Map();
            cache.set(roomId, roomCache);
        }
        return roomCache;
    };

    const getBreakoutCache = (roomId: RoomID) => {
        let roomCache = breakoutCache.get(roomId);
        if (!roomCache) {
            roomCache = new Map();
            breakoutCache.set(roomId, roomCache);
        }
        return roomCache;
    };

    const getSearchResultCache = (roomId: RoomID) => {
        let roomSeachCache = searchResultCache.get(roomId);
        if (!roomSeachCache) {
            roomSeachCache = new Map();
            searchResultCache.set(roomId, roomSeachCache);
        }
        return roomSeachCache;
    };

    const getHighlightedCharacters = (participantId: ParticipantID) => {
        for (const map of highlightedCharactersCache.values()) {
            const result = map.get(participantId);
            if (result) {
                return result;
            }
        }
        return [];
    };

    const isNewSearchQuery = (
        roomId: RoomID,
        key: GroupCacheKey | BreakoutCacheKey,
        searchQuery: string,
    ) => {
        return (
            searchResultCache.get(roomId)?.get(key)?.searchQuery !== searchQuery
        );
    };

    const clearGroupCache = (
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        cache.get(roomId)?.delete(toGroupCacheKey(groupKey, roomId));
        getSearchResultCache(roomId).delete(toGroupCacheKey(groupKey, roomId));

        breakoutCache.get(roomId)?.delete(toBreakoutCacheKey(groupKey, roomId));
        getSearchResultCache(roomId).delete(
            toBreakoutCacheKey(groupKey, roomId),
        );
    };

    const clearHighlightedCharactersCache = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        highlightedCharactersCache
            .get(toGroupCacheKey(groupKey, roomId))
            ?.delete(participant.uuid);
        if (
            highlightedCharactersCache.get(toGroupCacheKey(groupKey, roomId))
                ?.size === 0
        ) {
            highlightedCharactersCache.delete(
                toGroupCacheKey(groupKey, roomId),
            );
        }

        highlightedCharactersCache
            .get(toBreakoutCacheKey(groupKey, roomId))
            ?.delete(participant.uuid);
        if (
            highlightedCharactersCache.get(toBreakoutCacheKey(groupKey, roomId))
                ?.size === 0
        ) {
            highlightedCharactersCache.delete(
                toBreakoutCacheKey(groupKey, roomId),
            );
        }
    };

    const clearHighlightedCharacters = (roomId: RoomID) => {
        const participants = currentParticipants.values(roomId);
        if (!participants) {
            return;
        }
        for (const participant of participants) {
            participant.highlightedCharacters = [];
        }
        highlightedCharactersCache.clear();
    };

    const clearParticipantCache = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        cache
            .get(roomId)
            ?.delete(toGroupCacheKey(groupKey, roomId, participant.uuid));
        searchResultCache
            .get(roomId)
            ?.delete(toGroupCacheKey(groupKey, roomId, participant.uuid));
        // `inMeeting` participants are sorted based on the role, so when the role
        // changed, the cache needs to be invalidated
        if (groupKey === 'chair' || groupKey === 'guest') {
            const relatedGroups = groups
                .get(roomId)
                ?.getReversed(participant.uuid);
            if (relatedGroups?.has('inMeeting')) {
                clearGroupCache(roomId, 'inMeeting');
            }
        }
        clearHighlightedCharactersCache(participant, roomId, groupKey);
        clearBreakoutParticipantCache(participant, roomId, groupKey);
    };

    const clearRoomCache = (roomId: RoomID) => {
        cache.get(roomId)?.clear();
        breakoutCache.get(roomId)?.clear();
        searchResultCache.get(roomId)?.clear();
    };

    const clearBreakoutParticipantCache = (
        participant: Participant,
        roomId: RoomID,
        groupKey: ParticipantsFilterKey,
    ) => {
        // Group breakout changes should invalidate the breakout cache for all related
        if (groupKey === 'breakout') {
            const relatedGroups = groups
                .get(roomId)
                ?.getReversed(participant.uuid);
            if (relatedGroups) {
                for (const group of relatedGroups) {
                    breakoutCache
                        .get(roomId)
                        ?.delete(toBreakoutCacheKey(group, roomId));

                    searchResultCache
                        .get(roomId)
                        ?.delete(toBreakoutCacheKey(group, roomId));
                }
            }
        }
        breakoutCache
            .get(roomId)
            ?.delete(toBreakoutCacheKey(groupKey, roomId, participant.uuid));
        searchResultCache
            .get(roomId)
            ?.delete(toBreakoutCacheKey(groupKey, roomId, participant.uuid));
    };

    const updateParticipantGroups = (
        roomId: RoomID,
        participant: InMeetingParticipant,
    ) => {
        const {diffNew, diffOld} = diffGroups(
            groups.get(roomId)?.getReversed(participant.uuid) ?? new Set(),
            new Set(assignGroups(participant)),
        );
        for (const group of diffOld) {
            removeGroup(participant, roomId, group);
        }
        for (const group of diffNew) {
            addGroup(participant, roomId, group);
        }
    };

    const handleParticipantSync = ({
        id,
        participants,
    }: GetSignalType<InMeetingParticipantSignals['onParticipants']>) => {
        groups.delete(id);
        const pidsAfterSync: Set<ParticipantID> = new Set();
        const pidsBeforeSync = new Set(currentParticipants.keys(id));
        // Update the current participants
        for (const participant of participants) {
            const newParticipant = createInMeetingParticipant(
                participant,
                clientApis,
                getMedia,
                id,
            );
            pidsAfterSync.add(participant.uuid);
            setCurrentParticipant(id, newParticipant);

            const updated = pidsBeforeSync.has(participant.uuid);
            if (updated) {
                pushActivity(id, updateActivity(participant.uuid));
            }
        }
        // Update disconnected participants
        const disconnectedPids = difference(pidsBeforeSync, pidsAfterSync);
        const joinedPids = difference(pidsAfterSync, pidsBeforeSync);
        for (const pid of disconnectedPids) {
            const participant = currentParticipants.get(id, pid);
            if (!participant) {
                continue;
            }
            handleParticipantLeft({id, participant});
        }
        for (const pid of joinedPids) {
            pushActivity(id, joinActivity(pid));
        }
        clearRoomCache(id);
    };

    const handleParticipantJoined = ({
        id,
        participant,
    }: RoomParticipantEvent) => {
        const newParticipant = createInMeetingParticipant(
            participant,
            clientApis,
            getMedia,
            id,
        );
        const hasParticipant = currentParticipants.has(id, participant.uuid);
        setCurrentParticipant(id, newParticipant);
        // Ensure we don't push a duplicated join activity
        // See https://github.com/pexip/mcu/issues/44248
        if (!hasParticipant) {
            pushActivity(id, joinActivity(participant.uuid));
        }
        searchResultCache.get(id)?.clear();
    };

    const handleParticipantUpdate = ({
        id,
        participant,
    }: RoomParticipantEvent) => {
        const prevParticipant = currentParticipants.get(id, participant.uuid);
        // Skip when there is no such participant
        if (!prevParticipant) {
            return;
        }
        // Create the new participant object
        const newParticipant = createInMeetingParticipant(
            participant,
            clientApis,
            getMedia,
            id,
            getHighlightedCharacters(participant.uuid),
        );

        setCurrentParticipant(id, newParticipant);
        pushActivity(id, updateActivity(participant.uuid));

        // Clear search cache if display name has changed or if the API host status has changed in breakout room
        if (
            getDisplayName(prevParticipant) !==
                getDisplayName(newParticipant) ||
            (clientApis.conferenceStatus.get(id)?.breakout &&
                isAPIHost(prevParticipant) !== isAPIHost(newParticipant))
        ) {
            searchResultCache.get(id)?.clear();
        }
    };

    const handleParticipantLeft = ({id, participant}: RoomParticipantEvent) => {
        const deleted = currentParticipants.remove(id, participant.uuid);
        if (!deleted) {
            return;
        }
        const groupSet = groups.get(id)?.getReversed(participant.uuid);
        if (groupSet?.size) {
            for (const group of groupSet) {
                removeGroup(participant, id, group);
            }
        }
        oldParticipants.set(id, participant);
        pushActivity(id, leaveActivity(participant.uuid));
    };

    const handleBreakoutEnd = ({
        breakout_uuid,
    }: GetSignalType<InMeetingParticipantSignals['onBreakoutEnd']>) => {
        const current = currentParticipants.values(breakout_uuid);
        if (!current) {
            return;
        }
        groups.delete(breakout_uuid);
        cache.delete(breakout_uuid);
        breakoutCache.delete(breakout_uuid);
        if (current) {
            for (const participant of current) {
                oldParticipants.set(breakout_uuid, participant);
            }
        }
        currentParticipants.removeByPrimary(breakout_uuid);
        activityBuffer.remove(breakout_uuid);
        searchResultCache.get(breakout_uuid)?.clear();
    };

    const unsubscribes = [
        participantsSignals.onParticipants.add(handleParticipantSync),
        participantsSignals.onParticipantJoined.add(events => {
            for (const event of events) {
                handleParticipantJoined(event);
            }
        }),
        participantsSignals.onParticipantLeft.add(events => {
            for (const event of events) {
                handleParticipantLeft(event);
            }
        }),
        participantsSignals.onParticipantUpdated.add(events => {
            for (const event of events) {
                handleParticipantUpdate(event);
            }
        }),
        participantsSignals.onBreakoutEnd.add(handleBreakoutEnd),
    ];

    const sortParticipants =
        (sort: (a: Participant, b: Participant) => number) =>
        (roomId: RoomID) =>
        (a: ParticipantID, b: ParticipantID) => {
            const participantA = currentParticipants.get(roomId, a);
            const participantB = currentParticipants.get(roomId, b);
            // No comparison of non-existing participants
            if (participantA === undefined || participantB === undefined) {
                return 0;
            }
            return sort(participantA, participantB);
        };

    const sortByHandRaisedTime = sortParticipants((a, b) => {
        return a.handRaisedTime - b.handRaisedTime;
    });

    const sortByDisplayName = sortParticipants((a, b) => {
        return (getDisplayName(a) ?? '').localeCompare(
            getDisplayName(b) ?? '',
            undefined,
            {
                sensitivity: 'base',
            },
        );
    });

    const findParticipant = (
        roomId: RoomID,
        filterBy: ParticipantsFilterKey,
        participantID: ParticipantID,
    ) => {
        const exists = groups.get(roomId)?.has(filterBy, participantID);
        if (exists) {
            return new Set([participantID]);
        }
        return EMPTY_SET;
    };

    const findParticipants = (
        roomId: RoomID,
        filterBy: ParticipantsFilterKey,
        participantID: ParticipantID | undefined,
        key: GroupCacheKey | BreakoutCacheKey,
        searchQuery?: string,
    ) => {
        if (participantID !== undefined) {
            return findParticipant(roomId, filterBy, participantID);
        }
        let roomGroups = groups.get(roomId)?.get(filterBy);
        if (!roomGroups) {
            return EMPTY_SET;
        }

        // Apply search before sorting ---
        if (searchQuery) {
            const searchResult = participantSearcher.search(
                Array.from(roomGroups).flatMap(pid => {
                    const participant = currentParticipants.get(roomId, pid);
                    return participant ? [participant] : [];
                }),
                searchQuery,
            );

            for (const pid of roomGroups) {
                const participant = currentParticipants.get(roomId, pid);
                if (!participant) {
                    continue;
                }

                const searchMatch = searchResult.get(participant.uuid);
                if (searchMatch) {
                    participant.highlightedCharacters = searchMatch.matches;

                    const cached = highlightedCharactersCache.get(key);
                    if (cached) {
                        cached.set(participant.uuid, searchMatch.matches);
                    } else {
                        highlightedCharactersCache.set(
                            key,
                            new Map([
                                [
                                    searchMatch.participantId,
                                    searchMatch.matches,
                                ],
                            ]),
                        );
                    }
                } else {
                    participant.highlightedCharacters = [];
                    highlightedCharactersCache
                        .get(key)
                        ?.delete(participant.uuid);
                }
            }

            // Only keep matched participants
            roomGroups = new Set(searchResult.keys());
        }

        // --- Sorting logic remains unchanged ---
        switch (roomGroups.size) {
            case 0:
                return EMPTY_SET;
            case 1:
                return new Set(roomGroups);
            default: {
                switch (filterBy) {
                    case 'raisedHand': {
                        const sortBy = sortByHandRaisedTime(roomId);
                        return new Set(Array.from(roomGroups).sort(sortBy));
                    }
                    case 'inMeeting': {
                        const hosts: ParticipantID[] = [];
                        const guests: ParticipantID[] = [];
                        const sortBy = sortByDisplayName(roomId);
                        for (const pid of roomGroups) {
                            const participant = currentParticipants.get(
                                roomId,
                                pid,
                            );
                            if (!participant) {
                                continue;
                            }
                            if (participant.isHost) {
                                hosts.push(pid);
                            } else {
                                guests.push(pid);
                            }
                        }
                        return new Set([
                            ...hosts.sort(sortBy),
                            ...guests.sort(sortBy),
                        ]);
                    }
                    default: {
                        const sortBy = sortByDisplayName(roomId);
                        return new Set(Array.from(roomGroups).sort(sortBy));
                    }
                }
            }
        }
    };

    const getParticipantSet = ({
        roomId,
        participantID,
        filterBy,
        searchQuery,
        caching,
        cacheContext,
        key,
        filterFn,
    }: {
        roomId: RoomID;
        participantID?: ParticipantID;
        filterBy: ParticipantsFilterKey;
        searchQuery?: string;
        caching?: boolean;
        cacheContext: Map<GroupCacheKey | BreakoutCacheKey, Set<ParticipantID>>;
        key: GroupCacheKey | BreakoutCacheKey;
        filterFn?: (participant: InMeetingParticipant) => boolean;
    }) => {
        // 1. Use cached results if available and no search query
        if (caching) {
            const found = cacheContext?.get(key);
            if (found && (searchQuery === undefined || searchQuery === '')) {
                if (searchQuery === '') {
                    getSearchResultCache(roomId).delete(key);
                    clearHighlightedCharacters(roomId);
                }
                return found;
            }
            const searchCacheFound = getSearchResultCache(roomId).get(key);
            if (
                searchCacheFound &&
                searchQuery !== undefined &&
                !isNewSearchQuery(roomId, key, searchQuery)
            ) {
                return searchCacheFound.result;
            }
        }

        let result = findParticipants(
            roomId,
            filterBy,
            participantID,
            key,
            searchQuery,
        );

        // Apply custom filter if provided (for breakout)
        if (filterFn) {
            const filtered = new Set<ParticipantID>();
            for (const pid of result) {
                const participant = currentParticipants.get(roomId, pid);
                if (participant && filterFn(participant)) {
                    filtered.add(pid);
                }
            }
            result = filtered;
        }

        // Cache search results if searching
        if (
            caching &&
            searchQuery !== undefined &&
            isNewSearchQuery(roomId, key, searchQuery)
        ) {
            getSearchResultCache(roomId).set(key, {result, searchQuery});
            return result;
        }

        // Cache normal results
        if (caching) {
            cacheContext.set(key, result);
        }
        return result;
    };

    return Object.freeze({
        /**
         * Return a list of participants based on the criteria from `filterBy`.
         */
        get({
            roomId = clientApis.roomId,
            participantID,
            filterBy = 'none',
            searchQuery,
            caching = true,
        }: Partial<ParticipantsGetOptions> = {}) {
            return getParticipantSet({
                roomId,
                participantID,
                filterBy,
                searchQuery,
                caching,
                cacheContext: getCache(roomId),
                key: toGroupCacheKey(filterBy, roomId, participantID),
            });
        },
        /**
         * Return a list of participants who are NOT transferring and NOT a
         * host without media based on the criteria from `filterBy`.
         */
        getBreakout({
            roomId = clientApis.roomId,
            participantID,
            filterBy = 'none',
            caching = true,
            searchQuery,
        }: Partial<ParticipantsGetOptions> = {}) {
            return getParticipantSet({
                roomId,
                participantID,
                filterBy,
                searchQuery,
                caching,
                cacheContext: getBreakoutCache(roomId),
                key: toBreakoutCacheKey(filterBy, roomId, participantID),
                filterFn: participant =>
                    !participant.isTransferring && !isAPIHost(participant),
            });
        },
        getGroups(participantID: ParticipantID, roomId: RoomID) {
            return groups.get(roomId)?.getReversed(participantID);
        },
        getCurrent(participantID: ParticipantID, roomId: RoomID) {
            return currentParticipants.get(roomId, participantID);
        },
        getCurrentRoom(participantID: ParticipantID) {
            return currentParticipants.getReversed(participantID);
        },
        getDisconnected(participantID: ParticipantID, roomId?: RoomID) {
            if (roomId) {
                return oldParticipants.get(roomId, participantID);
            }
            const room = oldParticipants.getReversed(participantID);
            if (!room) {
                return;
            }
            return oldParticipants.get(room, participantID);
        },
        isDisconnected(participantID: ParticipantID, roomId?: RoomID) {
            if (roomId) {
                return Boolean(oldParticipants.has(roomId, participantID));
            }
            return oldParticipants.hasReversed(participantID);
        },
        consumeActivities(roomId: RoomID) {
            return activityBuffer.consume(roomId);
        },
        hasActivities(roomId: RoomID) {
            return activityBuffer.has(roomId);
        },
        dump() {
            const participants: RoomParticipantMap = new Map();
            oldParticipants.copyTo(participants);
            currentParticipants.copyTo(participants);
            return {
                participants,
                activities: activityBuffer.dump(),
            };
        },
        transfer(name: string, target: TransferTarget) {
            const selfUUID = clientApis.getMe()?.uuid;
            if (selfUUID) {
                pushActivity(
                    clientApis.roomId,
                    transferActivity(selfUUID, name, target),
                );
            }
            return this.dump();
        },
        release() {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
            unsubscribes.length = 0;
            currentParticipants.clear();
            oldParticipants.clear();
            groups.clear();
            cache.clear();
            breakoutCache.clear();
            activityBuffer.clear();
            searchResultCache.clear();
            highlightedCharactersCache.clear();
        },
    });
};
